import { KeyboardEventCheck } from '@Tools/KeyboardEventCheck';
import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// TYPES
import { TPopupPanel } from '@Managers/Redux/AppState/AppState.types';

// CSS
import styles from '@UILayer/UILayer.scss';

//MANAGERS
import EventsCenter from '@Managers/Events/EventsCenter';

// REDUX
import { getAutoPlayState, getGlobalStyles, setPopupPanel } from '@Managers/Redux/AppState/AppState';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';

import { EButtonEvents } from '@Managers/Events/ButtonEvents';

type TAutoButtonState = {
    active: boolean;
    showCounter: boolean;
};

const AutoButton: React.FC<{ uiFreeze: boolean }> = ({ uiFreeze }) => {
    const [state, setState] = useReducer((state: TAutoButtonState, newState: Partial<TAutoButtonState>) => ({ ...state, ...newState }), {
        active: true,
        showCounter: false
    });

    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const gameStoreAutoPlayState = useAppSelector(getAutoPlayState);
    const globalStyles = useAppSelector(getGlobalStyles);

    function buttonAction(e: React.MouseEvent<any>) {
        if (!uiFreeze || !gameStoreAutoPlayState.isSuspended) {
            if (e) e.stopPropagation();
            if (state.active) {
                dispatch(setPopupPanel(TPopupPanel.autoPlay));
            }
        }
    }

    function stopAutoPlay(e: React.MouseEvent<any>) {
        if (!uiFreeze) {
            if (e) e.stopPropagation();
            EventsCenter.emit(EButtonEvents.AUTO_STOP_BUTTON_PRESSED);
        }
    }

    const buttonCN = cn(
        globalStyles.orientation,
        globalStyles.wcaMode,
        globalStyles.isTablet,
        styles.autoplay_btn,
        styles.gameplay_btn,
        styles.gameplay_btn_type2,
        gameStoreAutoPlayState.isEnabled ? styles.active : null,
        uiFreeze ? styles.disabled : null,
        gameStoreAutoPlayState.isSuspended ? styles.disabled : null
    );

    return (
        <button
            tabIndex={0}
            className={buttonCN}
            id="ui_button_autoplay"
            aria-description={t('WCA_AUTOPLAY_SPINS_BUTTON')}
            aria-haspopup="dialog"
            onPointerDown={(e) => buttonAction(e)}
            onKeyDown={(e) => KeyboardEventCheck(e, buttonAction)}
        >
            {gameStoreAutoPlayState.isEnabled && (
                <>
                    <span
                        tabIndex={0}
                        id="ui_data_autoplay_counter_value"
                        className={cn(styles.counter, globalStyles.wcaMode)}
                        aria-valuenow={gameStoreAutoPlayState.currentCount}
                        aria-valuetext={JSON.stringify(gameStoreAutoPlayState.currentCount)}
                    >
                        {gameStoreAutoPlayState.currentCount}
                    </span>
                    <span
                        tabIndex={0}
                        role="button"
                        className={cn(styles.close, globalStyles.wcaMode)}
                        id="ui_button_autoplay_stop"
                        aria-description={t('WCA_STOP_AUTOPLAY_MODE')}
                        onPointerDown={(e) => stopAutoPlay(e)}
                        onKeyDown={(e) => KeyboardEventCheck(e, stopAutoPlay)}
                    ></span>
                </>
            )}
        </button>
    );
};

export default AutoButton;
